/**
 * Page publique de téléchargement d'un document B2B (Facture / Ticket / Autre).
 * Accédée via lien sécurisé `/document/<token>`.
 *
 * Affiche les métadonnées + propose le téléchargement direct du PDF/image.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} Ko` : `${(kb / 1024).toFixed(1)} Mo`;
};

const PublicDocumentPage = ({ token }) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/b2b/public/documents/${token}`);
        if (!cancelled) setDoc(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Lien invalide ou expiré.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-zinc-50">
        <Loader2 className="w-8 h-8 text-emerald-700 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-zinc-50 px-4">
        <div className="max-w-md bg-white border border-amber-200 rounded-xl p-6 text-center shadow-lg" data-testid="public-doc-error">
          <AlertTriangle className="w-12 h-12 text-amber-600 mx-auto mb-3" />
          <h1 className="text-xl font-serif font-semibold text-zinc-900 mb-2">Document indisponible</h1>
          <p className="text-sm text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  const fileUrl = `${API}/b2b/public/documents/${token}/file`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-zinc-50 px-4 py-8" data-testid="public-doc-page">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg border border-zinc-200 overflow-hidden">
        <div className="bg-[#1B4D3E] text-white px-6 py-5">
          <p className="text-xs uppercase tracking-wide opacity-80">Pharmacie Saint Ange Des Amis</p>
          <h1 className="text-2xl font-serif mt-1">{doc.doc_type_label}</h1>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-7 h-7 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-zinc-900 break-words">{doc.label || doc.file_name}</p>
              <p className="text-sm text-zinc-500 mt-0.5">
                {fmtSize(doc.file_size)} · {(doc.content_type || '').split('/')[1]?.toUpperCase() || 'PDF'}
              </p>
            </div>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-zinc-500">Clinique</div>
                <div className="text-zinc-900">{doc.clinic_name || '—'}</div>
              </div>
              {doc.requester_name && (
                <div>
                  <div className="text-xs text-zinc-500">Contact</div>
                  <div className="text-zinc-900">{doc.requester_name}</div>
                </div>
              )}
              <div className="col-span-2">
                <div className="text-xs text-zinc-500">Émis le</div>
                <div className="text-zinc-900">{fmtDateTime(doc.uploaded_at)}</div>
              </div>
            </div>
          </div>
          <a
            href={fileUrl}
            download={doc.file_name}
            className="block w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 rounded-md transition"
            data-testid="public-doc-download"
          >
            <Download className="w-5 h-5 inline mr-2 -mt-0.5" />
            Télécharger le document
          </a>
          <p className="text-xs text-zinc-400 text-center">
            Lien sécurisé. Pour toute question, contactez la Pharmacie Saint Ange Des Amis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicDocumentPage;
